import React, { useState, useEffect } from 'react';
import { Form, Input, Select, InputNumber, Switch, Alert } from 'antd';
import { DataType, GlobalVariableControlType } from '@kemu-io/hs-types';
import globalVariablesManager, { parsePath, buildFullPath } from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import { useTranslation } from '@hooks/index';
import RoundedModal from '@src/components/roundedModal/roundedModal';
import styles from './ConfigurationModal.module.css';

type Props = {
  visible: boolean;
  variableName: string | null;
  onClose: () => void;
}

/**
 * Modal for configuring global variable settings and control type
 * @param visible - Whether the modal is visible
 * @param variableName - The name of the variable being configured
 * @param onClose - Callback when modal should be closed
 */
const ConfigurationModal = ({ visible, variableName, onClose }: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
    const [currentControlType, setCurrentControlType] = useState<GlobalVariableControlType>('text');
  const [showTypeChangeWarning, setShowTypeChangeWarning] = useState(false);
  const t = useTranslation('GlobalVariablesPanel');

  const controlTypeOptions = [
    { label: t('ControlTypes.Anything'), value: 'anything' as GlobalVariableControlType },
    { label: t('ControlTypes.Number'), value: 'number' as GlobalVariableControlType },
    { label: t('ControlTypes.Slider'), value: 'slider' as GlobalVariableControlType },
    { label: t('ControlTypes.Text'), value: 'text' as GlobalVariableControlType },
    { label: t('ControlTypes.MultilineText'), value: 'multilineText' as GlobalVariableControlType },
    { label: t('ControlTypes.Dropdown'), value: 'dropdown' as GlobalVariableControlType },
    { label: t('ControlTypes.Image'), value: 'image' as GlobalVariableControlType },
    { label: t('ControlTypes.BinaryFile'), value: 'binaryFile' as GlobalVariableControlType },
    { label: t('ControlTypes.Checkbox'), value: 'checkbox' as GlobalVariableControlType },
    { label: t('ControlTypes.MultiSelect'), value: 'multiSelect' as GlobalVariableControlType },
    { label: t('ControlTypes.Button'), value: 'button' as GlobalVariableControlType },
  ];

  /**
   * Gets all available groups for the dropdown
   */
  const availableGroups = React.useMemo(() => {
    const allVariables = globalVariablesManager.getAllVariables();
    return allVariables
      .filter(v => globalVariablesManager.isGroupVariable(v))
      .sort((a, b) => {
        const aOrder = a.order ?? Infinity;
        const bOrder = b.order ?? Infinity;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return a.name.localeCompare(b.name);
      })
      .map(v => {
        // Extract display name from full path for label, but use full path as value
        const { name: displayName } = parsePath(v.name);
        return { label: displayName, value: v.name };
      });
  }, [visible, variableName]);

  useEffect(() => {
    if (visible && variableName) {
      const variable = globalVariablesManager.getGlobalVariable(variableName);
      if (variable) {
        setCurrentControlType(variable.controlType);
        // Extract display name for showing in title
        const { name: displayName } = parsePath(variable.name);
        form.setFieldsValue({
          controlType: variable.controlType,
          helpText: variable.helpText,
          group: variable.group,
          ...variable.controlSettings,
        });
      }
    }
  }, [visible, variableName, form]);

  const handleControlTypeChange = (value: GlobalVariableControlType) => {
    if (value !== currentControlType) {
      setShowTypeChangeWarning(true);
    }
    setCurrentControlType(value);

    // Reset form fields for the new control type
    const defaultSettings = getDefaultSettings(value);
    form.setFieldsValue(defaultSettings);
  };

  const getDefaultSettings = (controlType: GlobalVariableControlType) => {
    switch (controlType) {
      case 'number':
        return { value: 0 };
      case 'slider':
        return { min: 0, max: 100, step: 1 };
      case 'text':
        return { value: '', placeholder: 'Enter text' };
      case 'multilineText':
        return { value: '', placeholder: 'Enter text...' };
      case 'dropdown':
        return { options: ['Option 1', 'Option 2'], value: 'Option 1' };
      case 'image':
        return { value: null };
      case 'binaryFile':
        return { value: null };
      case 'checkbox':
        return { value: false };
      case 'multiSelect':
        return { options: ['Option 1', 'Option 2'], value: [] };
      case 'anything':
        return { value: null };
      case 'button':
        return {};
      default:
        return {};
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (!variableName) { return; }

      const variable = globalVariablesManager.getGlobalVariable(variableName);
      if (!variable) { return; }

      // Determine data type based on control type
      let dataType = DataType.String;
      switch (values.controlType) {
        case 'number':
        case 'slider':
          dataType = DataType.Number;
          break;
        case 'checkbox':
          dataType = DataType.Boolean;
          break;
        case 'image':
          dataType = DataType.ImageData;
          break;
        case 'binaryFile':
          dataType = DataType.BinaryFile;
          break;
        case 'multiSelect':
          dataType = DataType.Array;
          break;
        default:
          dataType = DataType.Anything;
      }

      // Extract control settings from form values
      const controlSettings = { ...values };
      delete controlSettings.controlType;
      delete controlSettings.helpText;

      // Parse current variable name to get display name
      const { name: displayName } = parsePath(variableName);

      // Get new group (if changed)
      const newGroup = values.group !== undefined ? values.group : variable.group;

      // Check if group has changed - if so, we need to delete the old variable first
      // to avoid duplication.
      const groupChanged = newGroup !== variable.group;

      if (groupChanged) {
        // Delete the old variable first (using the current full path name)
        globalVariablesManager.deleteGlobalVariable({ name: variableName, notify: false });
      }

      globalVariablesManager.setGlobalVariable({
        name: displayName,
        group: newGroup, // Use new group if changed
        value: showTypeChangeWarning ? null : variable.lastValue, // Clear value if type changed
        lastDataType: dataType,
        controlType: values.controlType,
        helpText: values.helpText,
        widgetId: variable.lastChangedByWidgetId ?? undefined,
        executionContext: variable.lastExecutionContext ?? undefined,
        controlSettings: controlSettings,
      });

      // Notify all variables in the parent group that the parent group has changed.
      if (variable.controlType === 'group') {
        await globalVariablesManager.notifyGroupMembersOfParentGroupChange(variable.name);
      }

      onClose();
    } catch (error) {
      console.error('Error updating variable:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setShowTypeChangeWarning(false);
    onClose();
  };

  const renderControlSpecificSettings = () => {
    switch (currentControlType) {
      case 'slider':
        return (
          <>
            <Form.Item
              name="min"
              label={t('ConfigurationModal.MinValue')}
              rules={[{ required: true, message: t('ConfigurationModal.MinRequired') }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="max"
              label={t('ConfigurationModal.MaxValue')}
              rules={[{ required: true, message: t('ConfigurationModal.MaxRequired') }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="step"
              label={t('ConfigurationModal.Step')}
              rules={[{ required: true, message: t('ConfigurationModal.StepRequired') }]}
            >
              <InputNumber style={{ width: '100%' }} min={0.01} step={0.01} />
            </Form.Item>
          </>
        );

      case 'dropdown':
        return (
          <Form.Item
            name="options"
            label={t('ConfigurationModal.Options')}
            rules={[{ required: true, message: t('ConfigurationModal.OptionsRequired') }]}
          >
            <Select
              mode="tags"
              placeholder={t('ConfigurationModal.OptionsPlaceholder')}
              style={{ width: '100%' }}
            />
          </Form.Item>
        );

      case 'multiSelect':
        return (
          <Form.Item
            name="options"
            label={t('ConfigurationModal.Options')}
            rules={[{ required: true, message: t('ConfigurationModal.OptionsRequired') }]}
          >
            <Select
              mode="tags"
              placeholder={t('ConfigurationModal.OptionsPlaceholder')}
              style={{ width: '100%' }}
            />
          </Form.Item>
        );

      case 'text':
        return (
          <Form.Item
            name="placeholder"
            label={t('ConfigurationModal.Placeholder')}
          >
            <Input placeholder={t('ConfigurationModal.PlaceholderPlaceholder')} />
          </Form.Item>
        );

      case 'multilineText':
        return (
          <Form.Item
            name="placeholder"
            label={t('ConfigurationModal.Placeholder')}
          >
            <Input placeholder={t('ConfigurationModal.PlaceholderPlaceholder')} />
          </Form.Item>
        );

      case 'checkbox':
        return (
          <Form.Item
            name="value"
            label={t('ConfigurationModal.DefaultValue')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        );

      default:
        return null;
    }
  };

  // Extract display name for title
  const displayName = variableName ? parsePath(variableName).name : '';

  return (
    <RoundedModal
      title={t('ConfigurationModal.Title', { name: displayName })}
      visible={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      footerClassName={styles.Footer}
      okBtnLabel={t.withBaseKey('CommonWords')('Save')}
      cancelBtnLabel={t.withBaseKey('CommonWords')('Cancel')}
      loadingOkBtn={loading}
      width={600}
    >
      <div className={styles.Container}>
        {showTypeChangeWarning && (
          <Alert
            message={t('ConfigurationModal.TypeChangeWarning')}
            type="warning"
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="controlType"
            label={t('ConfigurationModal.ControlType')}
            rules={[{ required: true, message: t('ConfigurationModal.ControlTypeRequired') }]}
            >
            <Select
              disabled={currentControlType === 'group'}
              placeholder={t('ConfigurationModal.ControlTypePlaceholder')}
              options={controlTypeOptions}
              onChange={handleControlTypeChange}
              popupClassName={styles.SelectDropdown}
            />
          </Form.Item>

          {renderControlSpecificSettings()}

          {availableGroups.length > 0 && (
            <Form.Item
              name="group"
              label={t('AddVariableModal.Group')}
            >
              <Select
                placeholder={t('AddVariableModal.GroupPlaceholder')}
                options={availableGroups}
                allowClear
                popupClassName={styles.SelectDropdown}
              />
            </Form.Item>
          )}

          <Form.Item
            name="helpText"
            label={t('ConfigurationModal.HelpText')}
          >
            <Input.TextArea
              placeholder={t('ConfigurationModal.HelpTextPlaceholder')}
              rows={3}
            />
          </Form.Item>
        </Form>
      </div>
    </RoundedModal>
  );
};

export default ConfigurationModal;
